
import { useEffect, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Logger from '@/utils/logger';
import { useAuth } from '@/context/AuthContext';

export interface QueueSyncStatus {
  isActive: boolean;
  lastTaskId: string | null;
  error: string | null;
}

/**
 * Invisible queue-based sync system
 * Replaces interval-based sync with server-side queue processing
 */
export const useQueueBasedSync = () => {
  Logger.debug('useQueueBasedSync hook has been initialized.');
  
  const { user, loading } = useAuth();
  const [syncStatus, setSyncStatus] = useState<QueueSyncStatus>({
    isActive: false,
    lastTaskId: null,
    error: null,
  });

  // Enqueue sync task function
  const enqueueSyncTask = useCallback(async (type: 'page_load' | 'auto_3min' | 'manual') => {
    Logger.debug(`enqueueSyncTask called with type: ${type}`);
    
    // Skip if auth is still loading
    if (loading) {
      Logger.debug(`Auth still loading, skipping ${type} sync task`);
      return null;
    }

    // Only authenticated users can enqueue sync tasks
    if (!user && type !== 'page_load') {
      Logger.debug(`User not authenticated, skipping ${type} sync task`);
      return null;
    }

    // Explicitly check session status before making RPC call
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      Logger.error(`Session validation failed for ${type} sync task:`, sessionError);
      setSyncStatus(prev => ({
        ...prev,
        error: 'Session validation failed'
      }));
      return null;
    }

    if (!session && type !== 'page_load') {
      Logger.debug(`No valid session found, skipping ${type} sync task`);
      return null;
    }

    // Helper function to make the RPC call
    const makeRPCCall = async () => {
      return await supabase.rpc('enqueue_sync_task', {
        p_task_type: type,
        p_task_metadata: {
          timestamp: new Date().toISOString(),
          source: 'client'
        }
      });
    };
    
    try {
      Logger.info(`Enqueuing ${type} sync task...`);
      
      const { data, error } = await makeRPCCall();

      if (error) {
        // Check for 401 error and implement retry logic
        if (error.code === 'PGRST301' || error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          Logger.warn(`Caught 401 error on enqueue_sync_task for ${type}. Attempting session refresh and retry.`);
          
          try {
            // Attempt to refresh the session
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              Logger.error(`Session refresh failed for ${type} sync task:`, refreshError);
              throw error; // Throw original 401 error
            }

            Logger.info(`Session refreshed successfully, retrying ${type} sync task`);
            
            // Retry the RPC call once more
            const { data: retryData, error: retryError } = await makeRPCCall();
            
            if (retryError) {
              Logger.error(`Retry failed for ${type} sync task after session refresh:`, retryError);
              throw retryError;
            }

            // Success on retry
            if (retryData) {
              Logger.info(`Task enqueued on retry: ${retryData}`);
              setSyncStatus(prev => ({
                ...prev,
                lastTaskId: retryData,
                error: null
              }));
            } else {
              Logger.info(`Task ${type} already queued on retry, skipping`);
            }

            return retryData;
            
          } catch (retryError: any) {
            Logger.error(`Critical: Failed to enqueue ${type} sync task even after session refresh:`, retryError);
            setSyncStatus(prev => ({
              ...prev,
              error: retryError.message
            }));
            throw retryError;
          }
        } else {
          // Non-401 error, throw immediately
          Logger.debug(`Supabase RPC error for ${type}:`, error);
          throw error;
        }
      }

      if (data) {
        Logger.info(`Task enqueued: ${data}`);
        setSyncStatus(prev => ({
          ...prev,
          lastTaskId: data,
          error: null
        }));
      } else {
        Logger.info(`Task ${type} already queued, skipping`);
      }

      return data;
    } catch (error: any) {
      Logger.error(`Failed to enqueue ${type} task:`, error);
      setSyncStatus(prev => ({
        ...prev,
        error: error.message
      }));
      throw error;
    }
  }, [user, loading]);

  // Manual sync trigger
  const triggerManualSync = useCallback(async () => {
    try {
      const taskId = await enqueueSyncTask('manual');
      if (taskId) {
        toast.success('Sync task queued successfully');
      } else {
        toast.info('Sync already in progress');
      }
      return taskId;
    } catch (error: any) {
      toast.error('Failed to queue sync task');
      throw error;
    }
  }, [enqueueSyncTask]);

  // Page load sync
  const triggerPageLoadSync = useCallback(async () => {
    Logger.debug('triggerPageLoadSync function has been called.');
    
    // Delay page load sync by 2 seconds
    setTimeout(async () => {
      Logger.debug('Page load sync timeout triggered - executing enqueueSyncTask');
      try {
        await enqueueSyncTask('page_load');
        Logger.debug('Page load sync task successfully enqueued');
      } catch (error) {
        Logger.warn('Page load sync failed:', error);
      }
    }, 2000);
  }, [enqueueSyncTask]);


  // Monitor sync queue status
  useEffect(() => {
    const monitorQueue = async () => {
      try {
        const { data, error } = await supabase
          .from('sync_queue')
          .select('id, status, type, created_at')
          .in('status', ['pending', 'processing'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        setSyncStatus(prev => ({
          ...prev,
          isActive: !!data,
          lastTaskId: data?.id || prev.lastTaskId
        }));
      } catch (error: any) {
        Logger.warn('Queue monitoring failed:', error);
      }
    };

    // Initial check
    monitorQueue();

    // Set up periodic monitoring
    const monitorInterval = setInterval(monitorQueue, 30000); // Check every 30 seconds

    return () => clearInterval(monitorInterval);
  }, []);

  return {
    syncStatus,
    enqueueSyncTask,
    triggerManualSync,
    triggerPageLoadSync,
  };
};
